/**
 * 1.7.0 stream C: the inspector's controls for the active format (docs/dev/formats-1.7.0-build.md): "Editing 9:16",
 * a clip's framing in the format (Fit / Fill / Blur, pan and zoom, keys with their ease, Same as primary, the upscale
 * badge; 1.7.1: in a format framed as a whole, Fit / Fill / Blur is the format's setting), a layer clip's placement and motion keys (position and size per format, opacity shared) and the "Shown in"
 * checkboxes of layer clips and graphics. Edits go through the ops with the build sheet's coalesce keys; sliders seal
 * the undo step on release.
 */
import { ChevronLeft, ChevronRight, Diamond, RotateCcw } from 'lucide-react';
import {
  FIT_LABELS, FIT_MODES, KEY_EASES, KEY_EASE_LABELS, PRIMARY_FORMAT, VIDEO_FIT_MODES, ZOOM_MAX, ZOOM_MIN, formatFitOf, framesToTimecode, formatTimecode, framingFor, hasOwnFraming,
  keysOf, ops, placeFor, snap, ticksPerFrame, ticksToFrames, upscaleFactor,
  type FitMode, type KeyEase, type KeyTarget, type LayerClip, type MotionKey, type SequenceDocument, type Size, type VideoClip,
} from '@prism/contracts';
import { useFormatView, type EditorApply } from './formatContext';
import {
  adjacentKeyTime, formatColor, framingEdit, framingValuesAt, hasOpacityKeys, hasPositionKeys, keyIndexAt, motionEdit, motionValuesAt, recordOf, sourceAt, spanOf,
} from './edit-formats';

const pct = (v: number) => `${Math.round(v * 100)}%`;

/** "Editing 9:16" in the active format's colour, when the sequence has formats. */
export function EditingFormat({ doc }: { doc: SequenceDocument }) {
  const view = useFormatView(doc);
  if (view.formats.length < 2) return null;
  return (
    <span className="editing-format" style={{ ['--format-color' as string]: formatColor(view.activeFormat.index) }} data-testid="editing-format" data-format={view.active}
      title="The inspector, the Reframe lane and the monitor edit this format; pick another in the format bar">
      Editing {view.activeFormat.label}{view.activeFormat.isPrimary ? ' · primary' : ''}
    </span>
  );
}

/** The upscale badge: shown when the picture is enlarged more than 1.5× in the frame ("×1.8"). */
export function UpscaleBadge({ factor }: { factor: number | null }) {
  if (factor === null || !(factor > 1.5)) return null;
  return (
    <span className="upscale-badge" data-testid="upscale-badge" title={`The source is enlarged ${factor.toFixed(2)}× in this format: it may look soft. Zoom out or use media with more pixels.`}>
      ×{factor.toFixed(1)}
    </span>
  );
}

function Slider({ id, label, value, min, max, step, fmt, onChange, seal, disabled }: {
  id: string; label: string; value: number; min: number; max: number; step: number; fmt: (v: number) => string; onChange: (v: number) => void; seal: () => void; disabled?: boolean;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label} <span className="faint mono">{fmt(value)}</span></label>
      <input id={id} type="range" min={min} max={max} step={step} value={value} disabled={disabled} data-testid={id}
        onChange={(e) => onChange(Number(e.target.value))} onPointerUp={seal} onKeyUp={seal} />
    </div>
  );
}

/** Previous key, add or remove the key at the playhead, next key; and the ease of the key at (or before) the playhead. */
function KeyRow({ prefix, label, times, atKey, canKey, onToggle, ease, onEase, setPlayhead, playhead, tpf, tc }: {
  prefix: string; label: string; times: number[]; atKey: boolean; canKey: boolean; onToggle: () => void;
  ease: KeyEase | null; onEase: (e: KeyEase) => void; setPlayhead: (t: number) => void; playhead: number; tpf: number; tc: (t: number) => string;
}) {
  const keys = times.map((t) => ({ recordTicks: t }));
  const prev = adjacentKeyTime(keys, playhead, -1, tpf);
  const next = adjacentKeyTime(keys, playhead, 1, tpf);
  return (
    <div className="key-row" data-testid={`${prefix}-keys`} data-count={times.length}>
      <button type="button" className="btn ghost icon sm" disabled={prev === null} onClick={() => prev !== null && setPlayhead(prev)} aria-label={`Previous ${label}`} title={prev !== null ? `Previous key at ${tc(prev)}` : 'No earlier key'} data-testid={`${prefix}-key-prev`}><ChevronLeft size={13} /></button>
      <button type="button" className={`btn ghost sm ${atKey ? 'on' : ''}`} disabled={!canKey} onClick={onToggle} aria-pressed={atKey} data-testid={`${prefix}-key-toggle`}
        title={!canKey ? 'Move the playhead into the clip to set keys' : atKey ? 'Remove the key at the playhead' : 'Add a key at the playhead with the values shown'}>
        <Diamond size={11} fill={atKey ? 'currentColor' : 'none'} /> {atKey ? `Remove ${label}` : `Add ${label}`}
      </button>
      <button type="button" className="btn ghost icon sm" disabled={next === null} onClick={() => next !== null && setPlayhead(next)} aria-label={`Next ${label}`} title={next !== null ? `Next key at ${tc(next)}` : 'No later key'} data-testid={`${prefix}-key-next`}><ChevronRight size={13} /></button>
      {ease && (
        <select className="select sm" value={ease} onChange={(e) => onEase(e.target.value as KeyEase)} aria-label="Ease after this key" title="How the values move from this key to the next" data-testid={`${prefix}-key-ease`}>
          {KEY_EASES.map((k) => <option key={k} value={k}>{KEY_EASE_LABELS[k]}</option>)}
        </select>
      )}
      <span className="faint key-count">{times.length ? `${times.length} key${times.length === 1 ? '' : 's'}` : 'No keys'}</span>
    </div>
  );
}

/**
 * Fit / Fill / Blur (Blur on the storyline only) of a clip in the active format, and for Fill its pan and zoom: the
 * static values, or with keys the values at the playhead (editing the key there, or adding one). "Same as primary"
 * returns a format to the primary's framing. The upscale badge says how much the source is enlarged.
 */
export function FormatFramingControls({ doc, clip, layer, apply, seal, playhead, setPlayhead, sourceSize }: {
  doc: SequenceDocument; clip: VideoClip | LayerClip; layer: boolean; apply: EditorApply; seal: () => void;
  playhead: number; setPlayhead: (t: number) => void; sourceSize: Size | null;
}) {
  const view = useFormatView(doc);
  const fid = view.active;
  const frame = view.activeFormat;
  const framing = framingFor(clip, fid);
  // A format framed as a whole (1.7.1): the control edits the format's fit; the clip keeps its pan, zoom and keys.
  const whole = layer ? undefined : formatFitOf(doc, fid);
  const fit: FitMode = whole ?? framing.fit ?? 'fit';
  const fill = fit === 'fill';
  const sameLook = (a: typeof framing) => JSON.stringify({ ...a, fit: undefined });
  const own = whole ? hasOwnFraming(clip, fid) && sameLook(framing) !== sameLook(framingFor(clip, PRIMARY_FORMAT)) : hasOwnFraming(clip, fid);
  // Same as primary under a whole-format fit returns the pan, zoom and keys only; the clip's own fit is kept for Per clip.
  const ownFit = clip.formatFraming?.[fid]?.fit;
  const resetPatch = whole && ownFit && ownFit !== (framingFor(clip, PRIMARY_FORMAT).fit ?? 'fit')
    ? { reset: ['framing' as const], fit: ownFit } : { reset: ['framing' as const] };
  const span = spanOf(doc, clip.id);
  const at = span ? sourceAt(span, playhead) : null;
  const tpf = ticksPerFrame(doc.frameRate);
  const t: KeyTarget = { clipId: clip.id, formatId: fid, kind: 'framing' };
  const keys = framing.keys ?? [];
  const keyed = keys.length > 0;
  const snapped = at !== null ? snap(at, doc.frameRate) : null;
  const atKey = snapped !== null ? keyIndexAt(keys, snapped) : -1;
  const values = framingValuesAt(clip, fid, at ?? keys[0]?.atTicks ?? clip.sourceInTicks);
  const tc = (x: number) => formatTimecode(framesToTimecode(ticksToFrames(x, doc.frameRate), doc.frameRate));
  const times = span ? keys.filter((k) => k.atTicks >= span.sourceIn && k.atTicks < span.sourceOut).map((k) => recordOf(span, k.atTicks)) : [];
  const easeIndex = atKey >= 0 ? atKey : snapped !== null ? keys.filter((k) => k.atTicks < snapped).length - 1 : -1;
  const ease = easeIndex >= 0 ? keys[easeIndex]!.ease : null;
  const setValue = (patch: { x?: number; y?: number; zoom?: number }) => {
    if (keyed && at === null) return;
    const e = framingEdit(doc, clip.id, fid, at ?? clip.sourceInTicks, patch);
    apply(e.fn, e.coalesce);
  };
  const toggleKey = () => {
    if (at === null) return;
    if (atKey >= 0) apply((d) => ops.deleteKey(d, t, atKey));
    else { const e = framingEdit(doc, clip.id, fid, at, {}, { forceKey: true }); apply(e.fn, e.coalesce); }
    seal();
  };
  // The frame a layer's picture fills is its size in the format.
  const box = layer ? { width: frame.width * placeFor(clip as LayerClip, fid).scale, height: frame.height * placeFor(clip as LayerClip, fid).scale } : frame;
  const factor = sourceSize ? upscaleFactor(sourceSize, box, { ...framing, fit }) : null;
  const modes = layer ? FIT_MODES : VIDEO_FIT_MODES;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="framing-controls" data-format={fid}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 -4px' }}>
        <p className="section-label" style={{ margin: 0 }}>Framing{view.formats.length > 1 ? ` · ${frame.label}` : ''}</p>
        <UpscaleBadge factor={factor} />
        <span style={{ flex: 1 }} />
        {fid !== PRIMARY_FORMAT && (own
          ? <button type="button" className="btn ghost sm" onClick={() => { apply((d) => ops.setFormatFraming(d, clip.id, fid, resetPatch)); seal(); }} data-testid="same-as-primary"
              title={whole ? `Pan, zoom and keys in ${frame.label} as in the primary again` : `Frame this clip in ${frame.label} as in the primary again`}><RotateCcw size={12} /> Same as primary</button>
          : <span className="faint" style={{ fontSize: 12 }} data-testid="framing-same-note">Same as primary</span>)}
      </div>
      <div className="segmented" role="radiogroup" aria-label={whole ? `Framing for the whole ${frame.label} format` : 'Framing'} style={{ alignSelf: 'flex-start' }}>
        {modes.map((m) => (
          <button key={m} role="radio" aria-checked={fit === m} aria-pressed={fit === m} data-testid={`fit-${m}`}
            onClick={() => apply((d) => (whole ? ops.updateFormat(d, fid, { fit: m }) : ops.setFormatFraming(d, clip.id, fid, { fit: m })))}
            title={m === 'fit' ? 'The whole picture, with bars where its shape differs from the frame' : m === 'fill' ? 'Cover the frame and crop the picture; Reframe moves the crop' : 'The whole picture over a blurred, filled copy of itself'}>{FIT_LABELS[m]}</button>
        ))}
      </div>
      {whole && (
        <span className="help whole-fit" data-testid="whole-format-fit">
          For the whole {frame.label} format: every storyline clip.{' '}
          <button type="button" className="link-button" onClick={() => { apply((d) => ops.updateFormat(d, fid, { fit: null })); seal(); }} data-testid="fit-per-clip"
            title={`Set Fit, Fill or Blur clip by clip in ${frame.label} again (each clip's own setting comes back)`}>Set per clip</button>
        </span>
      )}
      {fill && (
        <>
          <div className="field-row">
            <Slider id="reframe-x" label="Reframe left – right" value={values.x} min={-1} max={1} step={0.01} fmt={pct} onChange={(v) => setValue({ x: v })} seal={seal} disabled={keyed && at === null} />
            <Slider id="reframe-y" label="Reframe up – down" value={values.y} min={-1} max={1} step={0.01} fmt={pct} onChange={(v) => setValue({ y: v })} seal={seal} disabled={keyed && at === null} />
          </div>
          <Slider id="reframe-zoom" label="Zoom" value={values.zoom} min={ZOOM_MIN} max={ZOOM_MAX} step={0.01} fmt={(v) => `${v.toFixed(2)}×`} onChange={(v) => setValue({ zoom: v })} seal={seal} disabled={keyed && at === null} />
          <span className="help">{keyed
            ? at === null ? 'This clip has keys: move the playhead into it to edit the key there or add one.' : atKey >= 0 ? 'Editing the key at the playhead.' : 'Changing a value adds a key at the playhead.'
            : 'Left – right moves the crop of landscape media in a portrait frame; up – down of portrait media in a landscape frame. Zoom crops closer.'}</span>
          {(values.x || values.y || values.zoom !== 1) ? (
            <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => { setValue({ x: 0, y: 0, zoom: 1 }); seal(); }} data-testid="reframe-center" disabled={keyed && at === null}><RotateCcw size={13} /> Centre</button>
          ) : null}
        </>
      )}
      {(fill || keyed) && (
        <KeyRow prefix="framing" label="key" times={times} atKey={atKey >= 0} canKey={at !== null} onToggle={toggleKey} ease={ease}
          onEase={(e) => { if (easeIndex >= 0) apply((d) => ops.updateKey(d, t, easeIndex, { ease: e })); }} setPlayhead={setPlayhead} playhead={playhead} tpf={tpf} tc={tc} />
      )}
    </div>
  );
}

/**
 * Opacity, size and position of a layer clip in the active format: size and position are per format (static, or keys),
 * opacity is shared by every format (static, or the primary's opacity keys).
 */
export function LayerMotionControls({ doc, clip, apply, seal, playhead, setPlayhead }: {
  doc: SequenceDocument; clip: LayerClip; apply: EditorApply; seal: () => void; playhead: number; setPlayhead: (t: number) => void;
}) {
  const view = useFormatView(doc);
  const fid = view.active;
  const span = spanOf(doc, clip.id);
  const at = span ? sourceAt(span, playhead) : null;
  const snapped = at !== null ? snap(at, doc.frameRate) : null;
  const tpf = ticksPerFrame(doc.frameRate);
  const tc = (x: number) => formatTimecode(framesToTimecode(ticksToFrames(x, doc.frameRate), doc.frameRate));
  const vals = motionValuesAt(clip, fid, at ?? clip.sourceInTicks);
  const posKeyed = hasPositionKeys(clip, fid);
  const opKeyed = hasOpacityKeys(clip);
  const posT: KeyTarget = { clipId: clip.id, formatId: fid, kind: 'motion' };
  const opT: KeyTarget = { clipId: clip.id, formatId: PRIMARY_FORMAT, kind: 'motion' };
  const posKeys = (keysOf(doc, posT) as MotionKey[]);
  const opKeys = (keysOf(doc, opT) as MotionKey[]);
  const posAt = snapped !== null ? posKeys.findIndex((k) => k.atTicks === snapped && (k.x !== undefined || k.y !== undefined || k.scale !== undefined)) : -1;
  const opAt = snapped !== null ? opKeys.findIndex((k) => k.atTicks === snapped && k.opacity !== undefined) : -1;
  const visible = (k: MotionKey) => Boolean(span && k.atTicks >= span.sourceIn && k.atTicks < span.sourceOut);
  const posTimes = posKeys.filter((k) => (k.x !== undefined || k.y !== undefined || k.scale !== undefined) && visible(k)).map((k) => recordOf(span!, k.atTicks));
  const opTimes = opKeys.filter((k) => k.opacity !== undefined && visible(k)).map((k) => recordOf(span!, k.atTicks));
  const posEaseIndex = posAt >= 0 ? posAt : snapped !== null ? posKeys.filter((k) => k.atTicks < snapped).length - 1 : -1;
  const set = (patch: { x?: number; y?: number; scale?: number; opacity?: number }) => {
    const keyed = patch.opacity !== undefined ? opKeyed : posKeyed;
    if (keyed && at === null) return;
    const e = motionEdit(doc, clip.id, fid, at ?? clip.sourceInTicks, patch);
    apply(e.fn, e.coalesce);
  };
  const place = (p: { scale?: number; x?: number; y?: number }) => (fid === PRIMARY_FORMAT
    ? apply((d) => ops.updateLayerClip(d, clip.id, p))
    : apply((d) => ops.setFormatFraming(d, clip.id, fid, { place: p })));
  const formatOwn = fid !== PRIMARY_FORMAT && Boolean(clip.formatFraming?.[fid]?.place || clip.formatFraming?.[fid]?.motion);
  const toggle = (kind: 'position' | 'opacity') => {
    if (at === null) return;
    if (kind === 'position') {
      if (posAt >= 0) apply((d) => ops.updateKey(d, posT, posAt, { x: undefined, y: undefined, scale: undefined }));
      else { const e = motionEdit(doc, clip.id, fid, at, {}, { forceKey: true }); apply(e.fn, e.coalesce); }
    } else if (opAt >= 0) apply((d) => ops.updateKey(d, opT, opAt, { opacity: undefined }));
    else { const e = motionEdit(doc, clip.id, fid, at, { opacity: vals.opacity }, { forceKey: true }); apply(e.fn, e.coalesce); }
    seal();
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="layer-controls" data-format={fid}>
      <Slider id="opacity" label={`Opacity${view.formats.length > 1 ? ' (every format)' : ''}`} value={vals.opacity} min={0} max={1} step={0.01} fmt={pct} onChange={(v) => set({ opacity: v })} seal={seal} disabled={opKeyed && at === null} />
      <KeyRow prefix="opacity" label="opacity key" times={opTimes} atKey={opAt >= 0} canKey={at !== null} onToggle={() => toggle('opacity')} ease={opAt >= 0 ? opKeys[opAt]!.ease : null}
        onEase={(e) => { if (opAt >= 0) apply((d) => ops.updateKey(d, opT, opAt, { ease: e })); }} setPlayhead={setPlayhead} playhead={playhead} tpf={tpf} tc={tc} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <p className="section-label" style={{ margin: 0 }}>Size and position{view.formats.length > 1 ? ` · ${view.activeFormat.label}` : ''}</p>
        <span style={{ flex: 1 }} />
        {fid !== PRIMARY_FORMAT && (formatOwn
          ? <button type="button" className="btn ghost sm" onClick={() => { apply((d) => ops.setFormatFraming(d, clip.id, fid, { reset: ['place', 'motion'] })); seal(); }} data-testid="place-same-as-primary"><RotateCcw size={12} /> Same as primary</button>
          : <span className="faint" style={{ fontSize: 12 }}>Same as primary</span>)}
      </div>
      <Slider id="scale" label="Size" value={vals.scale} min={0.05} max={2} step={0.01} fmt={pct} onChange={(v) => set({ scale: v })} seal={seal} disabled={posKeyed && at === null} />
      <div className="field-row">
        <Slider id="posx" label="Left – right" value={vals.x} min={-1} max={1} step={0.01} fmt={pct} onChange={(v) => set({ x: v })} seal={seal} disabled={posKeyed && at === null} />
        <Slider id="posy" label="Up – down" value={vals.y} min={-1} max={1} step={0.01} fmt={pct} onChange={(v) => set({ y: v })} seal={seal} disabled={posKeyed && at === null} />
      </div>
      <KeyRow prefix="motion" label="position key" times={posTimes} atKey={posAt >= 0} canKey={at !== null} onToggle={() => toggle('position')} ease={posEaseIndex >= 0 ? posKeys[posEaseIndex]!.ease : null}
        onEase={(e) => { if (posEaseIndex >= 0) apply((d) => ops.updateKey(d, posT, posEaseIndex, { ease: e })); }} setPlayhead={setPlayhead} playhead={playhead} tpf={tpf} tc={tc} />
      {!posKeyed && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn ghost sm" onClick={() => { if (fid === PRIMARY_FORMAT) apply((d) => ops.updateLayerClip(d, clip.id, { opacity: 1, scale: 1, x: 0, y: 0 })); else place({ scale: 1, x: 0, y: 0 }); }}><RotateCcw size={13} /> Full frame</button>
          {([['Top left', -0.25, -0.25], ['Top right', 0.25, -0.25], ['Bottom left', -0.25, 0.25], ['Bottom right', 0.25, 0.25]] as const).map(([label, x, y]) => (
            <button key={label} className="btn ghost sm" onClick={() => place({ scale: 0.5, x, y })}>{label}</button>
          ))}
        </div>
      )}
      {posKeyed && <span className="help">{at === null ? 'Position keys: move the playhead into the clip to edit them.' : posAt >= 0 ? 'Editing the position key at the playhead.' : 'Changing size or position adds a key at the playhead.'}</span>}
    </div>
  );
}

/** Shown in: the formats a layer clip or a graphic appears in (hideIn lists where it does not). */
export function ShownIn({ doc, itemId, hideIn, apply, canEdit }: { doc: SequenceDocument; itemId: string; hideIn: string[] | undefined; apply: EditorApply; canEdit: boolean }) {
  const view = useFormatView(doc);
  if (view.formats.length < 2) return null;
  const hidden = new Set(hideIn ?? []);
  return (
    <div className="field" data-testid="shown-in">
      <label>Shown in</label>
      <div className="shown-in">
        {view.formats.map((f) => (
          <label key={f.id} className="toggle-row" style={{ ['--format-color' as string]: formatColor(f.index) }}>
            <input type="checkbox" checked={!hidden.has(f.id)} disabled={!canEdit} data-testid={`shown-in-${f.id}`}
              onChange={(e) => {
                const next = new Set(hidden);
                if (e.target.checked) next.delete(f.id); else next.add(f.id);
                apply((d) => ops.setHideIn(d, itemId, [...next]));
              }} />
            <span className="shown-in-swatch" aria-hidden />
            <span>{f.label}{f.isPrimary ? ' (primary)' : ''}</span>
          </label>
        ))}
      </div>
      {hidden.size > 0 && <span className="help">Left out of the {[...hidden].map((h) => view.formats.find((f) => f.id === h)?.label ?? h).join(', ')} preview and export.</span>}
    </div>
  );
}
